import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { JobLocation, Order } from '../../types';
import type { JobLocationMode, ValidationStatus } from '../../types/address-validation-types';
import { useValidateAddressMutation, useValidateCoordinatesMutation } from './orders-api-slice';

interface JobLocationDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order;
  onSubmit: (jobLocation: JobLocation, latitude: number, longitude: number) => Promise<void>;
  isSubmitting?: boolean;
}

const JobLocationDialog: React.FC<JobLocationDialogProps> = ({
  open,
  onClose,
  order,
  onSubmit,
  isSubmitting = false,
}) => {
  const existingLoc = order.jobLocation;

  const [mode, setMode] = useState<JobLocationMode>(existingLoc?.validationMode || 'address');
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>(
    existingLoc?.validationStatus === 'validated' ? 'validated' : 'none'
  );
  const [isValidating, setIsValidating] = useState(false);

  const [address1, setAddress1] = useState(existingLoc?.address1 || '');
  const [address2, setAddress2] = useState(existingLoc?.address2 || '');
  const [city, setCity] = useState(existingLoc?.city || '');
  const [state, setState] = useState(existingLoc?.state || '');
  const [zip, setZip] = useState(existingLoc?.zip || '');
  const [lat, setLat] = useState(existingLoc?.latitude?.toString() || '');
  const [lon, setLon] = useState(existingLoc?.longitude?.toString() || '');

  const [validateAddress] = useValidateAddressMutation();
  const [validateCoordinates] = useValidateCoordinatesMutation();

  useEffect(() => {
    if (open && existingLoc) {
      setAddress1(existingLoc.address1 || '');
      setAddress2(existingLoc.address2 || '');
      setCity(existingLoc.city || '');
      setState(existingLoc.state || '');
      setZip(existingLoc.zip || '');
      setLat(existingLoc.latitude?.toString() || '');
      setLon(existingLoc.longitude?.toString() || '');
      setMode(existingLoc.validationMode || 'address');
      setValidationStatus(existingLoc.validationStatus === 'validated' ? 'validated' : 'none');
    }
  }, [open, existingLoc]);

  const resetValidation = () => {
    setValidationStatus('none');
  };

  const handleValidateAddress = async () => {
    if (!address1 || !city || !state || !zip) return;
    setIsValidating(true);
    try {
      const result = await validateAddress({
        address1,
        address2,
        city,
        state,
        zip,
      }).unwrap();

      if (result.isValid) {
        setValidationStatus('validated');
        setLat(result.latitude.toString());
        setLon(result.longitude.toString());
        if (result.hasSuggestion && result.suggestedAddress) {
          setAddress1(result.suggestedAddress.address1);
          setCity(result.suggestedAddress.city);
          setState(result.suggestedAddress.state);
          setZip(result.suggestedAddress.zip);
        }
      } else if (!result.isValid && result.hasSuggestion && result.suggestedAddress) {
        setAddress1(result.suggestedAddress.address1);
        setCity(result.suggestedAddress.city);
        setState(result.suggestedAddress.state);
        setZip(result.suggestedAddress.zip);
        setValidationStatus('none');
      } else {
        setValidationStatus('failed');
      }
    } catch {
      setValidationStatus('failed');
    } finally {
      setIsValidating(false);
    }
  };

  const handleValidateCoordinates = async () => {
    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lon);
    if (isNaN(latNum) || isNaN(lonNum)) return;

    setIsValidating(true);
    try {
      const result = await validateCoordinates({
        latitude: latNum,
        longitude: lonNum,
      }).unwrap();

      if (result.isValid) {
        setValidationStatus('validated');
        if (result.validatedAddress) {
          setAddress1(result.validatedAddress.address1 || '');
          setCity(result.validatedAddress.city || '');
          setState(result.validatedAddress.state || '');
          setZip(result.validatedAddress.zip || '');
        }
      } else {
        setValidationStatus('failed');
      }
    } catch {
      setValidationStatus('failed');
    } finally {
      setIsValidating(false);
    }
  };

  const handleUpdate = async () => {
    if (validationStatus !== 'validated') return;
    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lon);
    if (isNaN(latNum) || isNaN(lonNum)) return;

    await onSubmit(
      {
        address1,
        address2,
        city,
        state,
        zip,
        latitude: latNum,
        longitude: lonNum,
        validationStatus: 'validated',
        validationMode: mode,
      },
      latNum,
      lonNum
    );
  };

  const prefillFromAddress = (
    src?: {
      address1?: string | null;
      address2?: string | null;
      city?: string | null;
      state?: string | null;
      zip?: string | null;
    } | null
  ) => {
    if (!src) return;
    setMode('address');
    setAddress1(src.address1 || '');
    setAddress2(src.address2 || '');
    setCity(src.city || '');
    setState(src.state || '');
    setZip(src.zip || '');
    resetValidation();
  };

  const billingAddress = order.billingAddress;
  const shippingAddress = order.shippingAddress;

  const isAddressComplete = address1 && city && state && zip;
  const isCoordValid =
    lat &&
    lon &&
    !isNaN(parseFloat(lat)) &&
    !isNaN(parseFloat(lon)) &&
    parseFloat(lat) >= -90 &&
    parseFloat(lat) <= 90 &&
    parseFloat(lon) >= -180 &&
    parseFloat(lon) <= 180;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <LocationOnIcon color="primary" />
          <Box>
            <Typography variant="h6">Edit Job Location</Typography>
            <Typography variant="body2" color="text.secondary">
              Order {order.orderNumber}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3, pb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter the jobsite address or coordinates, then validate to retrieve ASCE load ratings. The
          validated location will apply to all buildings in this order.
        </Typography>

        {/* Mode toggle */}
        <RadioGroup
          row
          value={mode}
          onChange={e => {
            setMode(e.target.value as JobLocationMode);
            resetValidation();
          }}
          sx={{ mb: 3 }}
        >
          <FormControlLabel
            value="address"
            control={<Radio />}
            label={
              <Typography variant="body1" fontWeight={600} color="primary">
                Address
              </Typography>
            }
          />
          <FormControlLabel
            value="coordinates"
            control={<Radio />}
            label={
              <Typography variant="body1" fontWeight={600} color="primary">
                Coordinates
              </Typography>
            }
            sx={{ ml: 2 }}
          />
        </RadioGroup>

        {/* Quick prefill shortcuts */}
        {(billingAddress?.address1 || shippingAddress?.address1) && (
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Prefill from:
            </Typography>
            {billingAddress?.address1 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => prefillFromAddress(billingAddress)}
              >
                Use Billing Address
              </Button>
            )}
            {shippingAddress?.address1 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => prefillFromAddress(shippingAddress)}
              >
                Use Shipping Address
              </Button>
            )}
            <Typography variant="caption" color="text.secondary">
              (You must still validate before saving)
            </Typography>
          </Box>
        )}

        {/* Address entry */}
        {mode === 'address' && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Address 1"
                required
                fullWidth
                value={address1}
                onChange={e => {
                  setAddress1(e.target.value);
                  resetValidation();
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Address 2"
                fullWidth
                value={address2}
                onChange={e => {
                  setAddress2(e.target.value);
                  resetValidation();
                }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="City"
                required
                fullWidth
                value={city}
                onChange={e => {
                  setCity(e.target.value);
                  resetValidation();
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="State"
                required
                fullWidth
                value={state}
                onChange={e => {
                  setState(e.target.value.toUpperCase().slice(0, 2));
                  resetValidation();
                }}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Zip"
                required
                fullWidth
                value={zip}
                onChange={e => {
                  setZip(e.target.value);
                  resetValidation();
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2} mt={1}>
                <Button
                  variant="outlined"
                  onClick={handleValidateAddress}
                  disabled={!isAddressComplete || isValidating}
                  startIcon={isValidating ? <CircularProgress size={18} /> : undefined}
                >
                  {validationStatus === 'validated' ? 'Revalidate Address' : 'Validate Address'}
                </Button>
                {validationStatus === 'validated' && (
                  <Box display="flex" alignItems="center" gap={0.5} color="success.main">
                    <CheckCircleIcon fontSize="small" />
                    <Typography variant="body2" fontWeight={500}>
                      Address validated
                    </Typography>
                  </Box>
                )}
                {validationStatus === 'failed' && (
                  <Box display="flex" alignItems="center" gap={0.5} color="error.main">
                    <ErrorOutlineIcon fontSize="small" />
                    <Typography variant="body2" fontWeight={500}>
                      Validation failed — please check the address and try again
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Coordinates entry */}
        {mode === 'coordinates' && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Latitude"
                required
                fullWidth
                type="number"
                value={lat}
                onChange={e => {
                  setLat(e.target.value);
                  resetValidation();
                }}
                inputProps={{ step: 'any', min: -90, max: 90 }}
                helperText="Range: -90 to 90"
                error={lat !== '' && (parseFloat(lat) < -90 || parseFloat(lat) > 90)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Longitude"
                required
                fullWidth
                type="number"
                value={lon}
                onChange={e => {
                  setLon(e.target.value);
                  resetValidation();
                }}
                inputProps={{ step: 'any', min: -180, max: 180 }}
                helperText="Range: -180 to 180"
                error={lon !== '' && (parseFloat(lon) < -180 || parseFloat(lon) > 180)}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2} mt={1}>
                <Button
                  variant="outlined"
                  onClick={handleValidateCoordinates}
                  disabled={!isCoordValid || isValidating}
                  startIcon={isValidating ? <CircularProgress size={18} /> : undefined}
                >
                  {validationStatus === 'validated'
                    ? 'Revalidate Coordinates'
                    : 'Validate Coordinates'}
                </Button>
                {validationStatus === 'validated' && (
                  <Box display="flex" alignItems="center" gap={0.5} color="success.main">
                    <CheckCircleIcon fontSize="small" />
                    <Typography variant="body2" fontWeight={500}>
                      Coordinates validated
                    </Typography>
                  </Box>
                )}
                {validationStatus === 'failed' && (
                  <Box display="flex" alignItems="center" gap={0.5} color="error.main">
                    <ErrorOutlineIcon fontSize="small" />
                    <Typography variant="body2" fontWeight={500}>
                      Validation failed — please check coordinates and try again
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Resolved coordinates panel */}
        {validationStatus === 'validated' && lat && lon && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: '#f0f7ff',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'primary.light',
            }}
          >
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              Resolved Coordinates
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Latitude
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {parseFloat(lat).toFixed(6)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Longitude
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {parseFloat(lon).toFixed(6)}
                </Typography>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              These coordinates will be sent to the ASCE API to retrieve Wind and Snow Load values.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" variant="text">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={validationStatus !== 'validated' || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {isSubmitting ? 'Fetching Load Ratings...' : 'Update Job Location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobLocationDialog;
